/* Collection menu — scroll-position restore between collection pages.
 *
 * Which scroller? Investigated in template-collection.css: neither
 * `.collection-layout__menu` (only `grid-area: menu`) nor
 * `.collection-menu-wrapper` (only `margin-top: 0`) sets any `overflow`, so
 * the sidebar does NOT scroll independently — the PAGE (window) is the
 * scroller. We therefore save/restore `window.scrollY`.
 *
 * Loaded WITHOUT `defer`, right after the menu markup, so the restore runs
 * before the content below the menu is parsed/painted (no visible jump).
 * The IIFE guard keeps a second inclusion (e.g. a future duplicate render)
 * from double-registering listeners.
 *
 * Stale-key protection: the payload stores WHERE the click happened (`from`,
 * the saving page's pathname) and WHERE it was going (`href`, the clicked
 * link). The restore only fires when the current page IS that destination
 * and the referrer IS the saving page — so a key orphaned by a navigation
 * that never consumes it (page/blog leaves without this script, new-tab
 * clicks) can never jump an unrelated later visit.
 */
(function () {
  if (window.vitrumCollectionMenuScroll) return;
  window.vitrumCollectionMenuScroll = true;

  var KEY = 'vitrum:cm-scroll';

  // The referrer must be the exact page that saved the key (same origin AND
  // same pathname) — stricter than a bare same-origin check, so the restore
  // is limited to the immediate next navigation from the saving page.
  function referrerMatches(fromPathname) {
    try {
      if (document.referrer === '' || typeof fromPathname !== 'string') return false;
      var referrer = new URL(document.referrer);
      return referrer.origin === window.location.origin && referrer.pathname === fromPathname;
    } catch (error) {
      return false;
    }
  }

  // The current page must be the destination of the click that saved the key.
  function isCurrentDestination(href) {
    try {
      return new URL(href, window.location.href).pathname === window.location.pathname;
    } catch (error) {
      return false;
    }
  }

  try {
    var raw = sessionStorage.getItem(KEY);
    if (raw !== null) {
      sessionStorage.removeItem(KEY);
      var saved = null;
      try {
        saved = JSON.parse(raw);
      } catch (parseError) {
        saved = null; // Corrupt/legacy payload — discard, no restore.
      }
      if (
        saved &&
        typeof saved.y === 'number' &&
        document.querySelector('.collection-menu') &&
        isCurrentDestination(saved.href) &&
        referrerMatches(saved.from)
      ) {
        var top = Math.round(saved.y) || 0;
        window.scrollTo(0, top);
        // The document may still be too short to reach `top` mid-parse. Only
        // if the synchronous restore did NOT take, re-apply it once the full
        // page height exists — and drop that re-apply on the first sign of
        // user intent so a user already scrolling is never yanked back.
        var applied = Math.abs(window.scrollY - top) <= 1;
        if (!applied && document.readyState === 'loading') {
          var cancelled = false;
          var cancel = function () {
            cancelled = true;
          };
          ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (type) {
            window.addEventListener(type, cancel, { once: true, passive: true });
          });
          document.addEventListener(
            'DOMContentLoaded',
            function () {
              if (!cancelled && Math.abs(window.scrollY - top) > 1) window.scrollTo(0, top);
            },
            { once: true }
          );
        }
      }
    }
  } catch (error) {
    // sessionStorage unavailable — degrade to no restore.
  }

  document.addEventListener('click', function (event) {
    // Only plain primary-button clicks navigate THIS tab; modified clicks
    // (new tab/window) or default-prevented clicks would orphan the key here.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    var link = event.target.closest('.collection-menu a[href]');
    if (!link) return;
    try {
      sessionStorage.setItem(
        KEY,
        JSON.stringify({
          y: Math.round(window.scrollY),
          href: link.href,
          from: window.location.pathname,
        })
      );
    } catch (error) {
      // Ignore storage failures; navigation proceeds normally.
    }
  });

  // Explicit aria-expanded sync (spec §8: "ADD explicit aria-expanded sync
  // only if trivial"). Needed because global.js MenuDrawer.onSummaryClick
  // sets aria-expanded="true" 100ms after EVERY summary click inside the
  // mobile drawer — including clicks that CLOSE a details — and never touches
  // a sibling auto-closed via the shared details[name]. `toggle` fires for
  // both cases; the delayed re-sync outlasts that 100ms write. `toggle` does
  // not bubble, hence the capture-phase listener.
  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target;
      if (!details || !details.matches || !details.matches('.collection-menu details')) return;
      var summary = details.querySelector('summary');
      if (!summary) return;
      var sync = function () {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      };
      sync();
      setTimeout(sync, 150);
    },
    true
  );
})();
